import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BsChevronDown, BsThreeDotsVertical } from 'react-icons/bs';
import { AiOutlineStar, AiFillStar } from 'react-icons/ai';
import styles from './css/index.module.css';
import { Dropdown } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import { TiArrowUnsorted } from 'react-icons/ti';
import { BiFilterAlt } from 'react-icons/bi';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { format, differenceInDays, isAfter } from 'date-fns';
import { getProfileNameComponent, htmlToPlainText } from '../../utils/commonUtils';
import Select from 'react-select/creatable';
import {
    DeleteBulkTask,
    DeleteTask,
    GetTask,
    GetTaskComment,
    PatchTask,
    PostTask,
    PostTaskComment,
    UpdateBulkTask,
    UploadCSVFile,
    DownloadCSVTemplate,
} from '../../redux/taskSlice';
import { GetProjectUsers } from '../../redux/teamSlice';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import DatePicker from 'react-datepicker';
import { GetProjectTaskWorkstreams, GetProjectTaskTags } from '../../redux/taskSlice';
import { GetDataroomItemInfo, GetFolderDefaults } from '../../redux/dataroomSlice';

import { RxCross2 } from 'react-icons/rx';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import SafeHTML from '../SafeHTML';

const TaskInterface = () => {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state.projectData);
    const { taskData, uploadCSVBtnLoading, downloadTemplateLoading } = useSelector(state => state.task);
    const [modalVisible, setModalVisible] = React.useState(false);
    const [viewModalVisible, setViewModalVisible] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState(null);
    const [filteredData, setFilteredData] = React.useState(taskData || []);
    const [currentFilters, setCurrentFilters] = React.useState({});

    const handleUploadCSV = React.useCallback(
        async event => {
            const file = event.target.files[0];
            if (file && !uploadCSVBtnLoading) {
                const formData = new FormData();
                formData.append('file', file);
                try {
                    await dispatch(UploadCSVFile(formData));
                } catch (error) {
                    console.error('Error dispatching UploadCSVFile:', error);
                }
                // Reset the file input
                event.target.value = '';
            } else {
                console.log('File upload skipped:', { file: !!file, uploadCSVBtnLoading });
            }
        },
        [dispatch, uploadCSVBtnLoading],
    );

    const handleDownloadTemplate = React.useCallback(() => {
        dispatch(DownloadCSVTemplate());
    }, [dispatch]);

    const applyFiltersAndSort = React.useCallback((data, filters) => {
        if (!data) return [];

        let filteredTasks = [...data];

        // Apply filters if they exist
        if (filters && Object.keys(filters).length > 0) {
            if (filters?.workstream && filters?.workstream.length > 0) {
                filters?.workstream?.forEach(item => {
                    if (!item.value) {
                        filteredTasks = filteredTasks.filter(d => d.workstream_repr !== item?.label);
                    }
                });
            }

            if (filters?.status && filters?.status.length > 0) {
                const pendingFilter = filters.status.find(item => item.label === 'PENDING');
                const completeFilter = filters.status.find(item => item.label === 'COMPLETE');

                if (pendingFilter && !pendingFilter.value) {
                    filteredTasks = filteredTasks.filter(d => d.status !== 'PENDING');
                }
                if (completeFilter && !completeFilter.value) {
                    filteredTasks = filteredTasks.filter(d => d.status !== 'COMPLETE');
                }
            }

            if (filters?.tag && filters?.tag.length > 0) {
                filters?.tag?.forEach(item => {
                    if (!item.value) {
                        filteredTasks = filteredTasks.filter(d => d.tag_repr !== item?.label);
                    }
                });
            }

            if (filters?.priority && filters?.priority.length > 0) {
                filters?.priority?.forEach(item => {
                    if (!item.value) {
                        filteredTasks = filteredTasks.filter(d => d.priority !== item?.label);
                    }
                });
            }

            if (filters?.assigned_to && filters?.assigned_to.length > 0) {
                filters?.assigned_to?.forEach(item => {
                    if (!item.value) {
                        filteredTasks = filteredTasks.filter(d => d?.assigned_to !== item?.label);
                    }
                });
            }

            if (filters?.assigned_to_offline && filters?.assigned_to_offline.length > 0) {
                filters?.assigned_to_offline?.forEach(item => {
                    if (!item.value) {
                        filteredTasks = filteredTasks.filter(d => d?.assigned_to_offline !== item?.label);
                    }
                });
            }
        }

        // Apply sorting
        filteredTasks.sort((a, b) => {
            // First priority: starred tasks always come first
            if (a.starred && !b.starred) return -1;
            if (!a.starred && b.starred) return 1;

            // Second priority: sort by deadline for non-starred tasks
            let da = new Date(a?.deadline),
                db = new Date(b?.deadline);
            return da - db;
        });

        return filteredTasks;
    }, []);

    React.useEffect(() => {
        const filteredAndSorted = applyFiltersAndSort(taskData, currentFilters);
        setFilteredData(filteredAndSorted);
    }, [taskData, currentFilters, applyFiltersAndSort]);

    React.useEffect(() => {
        if (selectedProject) {
            dispatch(GetTask());
            dispatch(GetProjectUsers(selectedProject?.id));
            dispatch(GetProjectTaskWorkstreams());
            dispatch(GetProjectTaskTags());
        }
    }, [selectedProject, dispatch]);

    return (
        <div>
            <div className={styles.headerSection}>
                <div className="d-flex align-items-center justify-content-between">
                    <h5 className={styles.headerTitle}>Tasks</h5>
                </div>
            </div>

            <>
                <div className="d-flex align-items-center justify-content-between">
                    {' '}
                    <div className={styles.menuButton}>TASK LIST</div>{' '}
                    <div className="d-flex align-items-center">
                        <div className="me-2">
                            <div className="d-flex align-items-center gap-2">
                                <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUploadCSV} className="d-none" id="csv-upload" disabled={uploadCSVBtnLoading} />
                                <button
                                    onClick={() => document.getElementById('csv-upload').click()}
                                    disabled={uploadCSVBtnLoading}
                                    className={`btn btn-primary btn-sm ${styles.smallButton}`}>
                                    Upload From CSV {uploadCSVBtnLoading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                                </button>
                                <button onClick={handleDownloadTemplate} disabled={downloadTemplateLoading} className={`btn btn-primary btn-sm ${styles.smallButton}`}>
                                    Download Template {downloadTemplateLoading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                                </button>
                            </div>
                        </div>
                        <button onClick={() => setModalVisible(true)} type="button" className={`btn btn-primary btn-sm ${styles.smallButton}`}>
                            Add New Task
                        </button>
                    </div>
                </div>
                <div className={styles.tabViewBody}>
                    <TaskTable
                        filteredData={filteredData}
                        setFilteredData={setFilteredData}
                        setModalVisible={setModalVisible}
                        setSelectedRow={setSelectedRow}
                        setViewModalVisible={setViewModalVisible}
                        currentFilters={currentFilters}
                        setCurrentFilters={setCurrentFilters}
                        applyFiltersAndSort={applyFiltersAndSort}
                    />
                </div>
            </>
            <TaskFormSidebar show={modalVisible} setShow={setModalVisible} selectedRow={selectedRow} setSelectedRow={setSelectedRow} />
            <TaskViewSidebar
                show={viewModalVisible}
                setShow={setViewModalVisible}
                selectedRow={selectedRow}
                setSelectedRow={setSelectedRow}
                setEditModalVisible={setModalVisible}
            />
        </div>
    );
};

const ColumnFilters = ({ columnState, setColumnState }) => {
    const DropdownToggleCol = React.forwardRef(({ onClick }, ref) => (
        <div
            ref={ref}
            onClick={e => {
                e.preventDefault();
                onClick(e);
            }}
            className={styles.filterSelector}>
            <span>Column View</span>
            &nbsp; &nbsp;
            <BsChevronDown size={10} color="#829ab1" />
        </div>
    ));

    const handleChangeFilter = (label, key, value) => {
        var data = { ...columnState };
        data[key].value = value;
        setColumnState(data);
    };
    const handleClickAll = value => {
        var data = { ...columnState };
        Object.keys(columnState).forEach(key => {
            data[key].value = value;
        });
        setColumnState(data);
    };
    const handleGetAllValue = () => {
        return Object.keys(columnState)?.find(key => columnState[key]?.value === false) ? false : true;
    };

    return (
        <div>
            <Dropdown>
                <Dropdown.Toggle as={DropdownToggleCol} id="dropdown-basic"></Dropdown.Toggle>

                <Dropdown.Menu>
                    <div style={{ width: 350 }} className={styles.filterBody}>
                        <div className="col-sm-12 col-md-6 d-flex align-items-center mt-2">
                            <input checked={handleGetAllValue()} onChange={e => handleClickAll(e.target.checked)} type="checkbox" id="flexCheckDefault" className=" me-3" />
                            <label className={styles.checkLabel}>View All</label>
                        </div>
                        <hr className="mb-2 mt-3" />
                        <div className="row">
                            {Object.keys(columnState)?.map(item => (
                                <div key={columnState[item]?.id} className="col-sm-12 col-md-6 d-flex align-items-center mt-2">
                                    <input
                                        checked={columnState[item]?.value}
                                        onChange={e => handleChangeFilter(columnState[item]?.label, item, e.target.checked)}
                                        type="checkbox"
                                        id="flexCheckDefault"
                                        className=" me-3"
                                    />
                                    <label className={styles.checkLabel + ' ' + styles.smallFont}>{columnState[item]?.label}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

const TaskTable = ({ filteredData, setModalVisible, setSelectedRow, setViewModalVisible, setFilteredData, currentFilters, setCurrentFilters, applyFiltersAndSort }) => {
    const dispatch = useDispatch();

    const [columnState, setColumnState] = React.useState({
        priority: { label: 'Priority', value: true },
        task: { label: 'Task', value: true },
        workstream: { label: 'Workstream', value: true },
        assigned_to: { label: 'Assigned to', value: true },
        tag: { label: 'Tag', value: true },
        added_on: { label: 'Added on', value: true },
        deadline: { label: 'Deadline', value: true },
        status: { label: 'Status', value: true },
    });

    const { projectUsersAll } = useSelector(state => state.team);
    const { items } = useSelector(state => state.dataroom);
    const { taskData } = useSelector(state => state.task);
    const [deadlineSort, setDeadlinSort] = React.useState('asc');
    const [addedOnSort, setAddedOnSort] = React.useState('asc');
    const [selectedRows, setSelectedRows] = React.useState([]);
    const [tableFilters, setTableFilters] = React.useState({});
    const filtersInitialized = React.useRef(false);

    const handleSelectAllFilter = val => {
        if (val) {
            let ids = [];
            filteredData?.forEach(tab => {
                if (!tab?.active) ids.push(tab?.id);
            });
            setSelectedRows(ids);
        } else setSelectedRows([]);
    };

    const PriorityToggle = React.forwardRef(({ onClick }, ref) => (
        <div
            ref={ref}
            onClick={e => {
                e.preventDefault();
                onClick(e);
            }}
            style={{ width: 90 }}
            className={styles.bulkSelector + ' me-2'}>
            <span>Priority</span>
            &nbsp; &nbsp;
            <BsChevronDown size={10} color="var(--Brand-Secondary)" />
        </div>
    ));
    const AssinedToToggle = React.forwardRef(({ onClick }, ref) => (
        <div
            ref={ref}
            onClick={e => {
                e.preventDefault();
                onClick(e);
            }}
            style={{ width: 114 }}
            className={styles.bulkSelector + ' me-2'}>
            <span>Assigned to</span>
            &nbsp; &nbsp;
            <BsChevronDown size={10} color="var(--Brand-Secondary)" />
        </div>
    ));
    const TagToggle = React.forwardRef(({ onClick }, ref) => (
        <div
            ref={ref}
            onClick={e => {
                e.preventDefault();
                onClick(e);
            }}
            style={{ width: 68 }}
            className={styles.bulkSelector + ' me-2'}>
            <span>Tag</span>
            &nbsp; &nbsp;
            <BsChevronDown size={10} color="var(--Brand-Secondary)" />
        </div>
    ));
    const StatusToggle = React.forwardRef(({ onClick }, ref) => (
        <div
            ref={ref}
            onClick={e => {
                e.preventDefault();
                onClick(e);
            }}
            style={{ width: 84 }}
            className={styles.bulkSelector + ' me-2'}>
            <span>Status</span>
            &nbsp; &nbsp;
            <BsChevronDown size={10} color="var(--Brand-Secondary)" />
        </div>
    ));

    const handleChangeFilters = (label, category, value) => {
        let data = tableFilters;
        data[category] = data[category]?.map(item => {
            if (item.label === label) {
                item.value = value;
            }
            return item;
        });
        setTableFilters({ ...data });
        setCurrentFilters({ ...data }); // Also update parent filters
        handleUpdateFilters(data);
    };

    const handleUpdateFilters = updatedFilters => {
        const filteredData = applyFiltersAndSort(taskData, updatedFilters);
        setFilteredData(filteredData);
    };

    const FilterIcon = React.forwardRef(({ onClick }, ref) => (
        <BiFilterAlt
            ref={ref}
            onClick={e => {
                e.preventDefault();
                onClick(e);
            }}
            className="me-2"
            color="#829ab1"
            size={14}
            role="button"
        />
    ));

    React.useEffect(() => {
        if (taskData) {
            let filters = generateFiltersFromTasks(taskData);
            let formattedFilters = {};

            // If filters haven't been initialized yet, initialize with all true
            if (!filtersInitialized.current) {
                Object.keys(filters).forEach(key => {
                    formattedFilters[key] = filters[key].map(item => ({
                        label: item,
                        value: true,
                    }));
                });
                setTableFilters(formattedFilters);
                filtersInitialized.current = true;
            } else {
                // Use functional update to avoid stale closure issues
                setTableFilters(prevTableFilters => {
                    let formattedFilters = {};
                    let hasChanges = false;

                    Object.keys(filters).forEach(key => {
                        formattedFilters[key] = filters[key].map(item => {
                            const existingFilter = prevTableFilters[key]?.find(f => f.label === item);
                            return {
                                label: item,
                                value: existingFilter ? existingFilter.value : true,
                            };
                        });

                        // Check if this key has changes (new options or missing options)
                        if (
                            formattedFilters[key].length !== (prevTableFilters[key]?.length || 0) ||
                            formattedFilters[key].some(item => !prevTableFilters[key]?.find(f => f.label === item.label))
                        ) {
                            hasChanges = true;
                        }
                    });

                    // Only return new state if there are actual changes
                    return hasChanges ? formattedFilters : prevTableFilters;
                });
            }
        }
    }, [taskData]);
    const handleSortAdded = () => {
        setAddedOnSort(addedOnSort === 'asc' ? 'desc' : 'asc');
        var data = [...filteredData];
        data.sort((a, b) => {
            let da = new Date(a.created),
                db = new Date(b.created);
            return addedOnSort === 'asc' ? db - da : da - db;
        });
        setFilteredData(data);
    };
    const handleSortDeadline = () => {
        setDeadlinSort(deadlineSort === 'asc' ? 'desc' : 'asc');
        var data = [...filteredData];
        data.sort((a, b) => {
            let da = new Date(a.deadline),
                db = new Date(b.deadline);
            return deadlineSort === 'asc' ? db - da : da - db;
        });
        setFilteredData(data);
    };

    const handleDeleteTasks = () => {
        dispatch(DeleteBulkTask({ ids: selectedRows }));
    };
    const handleChangeBulkPriority = val => {
        dispatch(
            UpdateBulkTask({
                ids: selectedRows,
                fields_and_values: [
                    {
                        priority: val,
                    },
                ],
            }),
        );
    };
    const handleChangeBulkAssigned = val => {
        dispatch(
            UpdateBulkTask({
                ids: selectedRows,
                fields_and_values: [
                    {
                        assigned_to_id: val,
                    },
                ],
            }),
        );
    };
    const handleChangeBulkTag = val => {
        dispatch(
            UpdateBulkTask({
                ids: selectedRows,
                fields_and_values: [
                    {
                        tag_repr: val,
                    },
                ],
            }),
        );
    };
    const handleChangeBulkStatus = val => {
        dispatch(
            UpdateBulkTask({
                ids: selectedRows,
                fields_and_values: [
                    {
                        status: val,
                    },
                ],
            }),
        );
    };
    const handleChangeBulkDeadline = val => {
        dispatch(
            UpdateBulkTask({
                ids: selectedRows,
                fields_and_values: [
                    {
                        deadline: format(new Date(val), 'yyyy-MM-dd'),
                    },
                ],
            }),
        );
    };
    const handleGetProjectName = id => {
        let user = projectUsersAll.find(item => item.value.user_id === id);
        return (
            <span>
                {user?.value?.full_name} ({user?.value?.organisation || 'N/A'})
            </span>
        );
    };

    const DeadlineDatePicker = React.forwardRef(({ onClick }, ref) => (
        <div
            ref={ref}
            onClick={e => {
                e.preventDefault();
                onClick(e);
            }}
            style={{ width: 98 }}
            className={styles.bulkSelector + ' me-2'}>
            <span>Deadline</span>
            &nbsp; &nbsp;
            <BsChevronDown size={10} color="var(--Brand-Secondary)" />
        </div>
    ));
    const setAllValuesToTrue = (obj, key) => {
        const newFilters = { ...obj };
        if (newFilters.hasOwnProperty(key) && Array.isArray(newFilters[key])) {
            newFilters[key] = newFilters[key].map(item => ({
                ...item,
                value: true,
            }));
        }
        if (key === 'assigned_to' && newFilters.hasOwnProperty('assigned_to_offline')) {
            newFilters['assigned_to_offline'] = newFilters['assigned_to_offline'].map(item => ({
                ...item,
                value: true,
            }));
        }
        setTableFilters(newFilters);
        setCurrentFilters(newFilters); // Also update parent filters
        handleUpdateFilters(newFilters);
    };
    const filterItemsWithValueFalse = obj => {
        const filteredItems = {};

        for (const key in obj) {
            if (Array.isArray(obj[key])) {
                const filteredArray = obj[key].filter(item => item.value === false);
                if (filteredArray.length > 0) {
                    if (key === 'assigned_to_offline') {
                        // Combine assigned_to_offline with assigned_to
                        if (!filteredItems['assigned_to']) {
                            filteredItems['assigned_to'] = [];
                        }
                        filteredItems['assigned_to'] = [...filteredItems['assigned_to'], ...filteredArray];
                    } else {
                        filteredItems[key] = filteredArray;
                    }
                }
            }
        }

        return filteredItems;
    };
    const generateFiltersFromTasks = (tasks, filters) => {
        let newFilters = {
            priority: [],
            assigned_to: [],
            assigned_to_offline: [],
            tag: [],
            workstream: [],
            status: [],
        };

        tasks?.forEach(task => {
            if (task?.priority && !newFilters.priority.includes(task?.priority)) {
                newFilters.priority.push(task?.priority);
            }
            if (task?.assigned_to && !newFilters.assigned_to.includes(task?.assigned_to)) {
                newFilters.assigned_to.push(task?.assigned_to);
            }
            if (task?.assigned_to_offline && !newFilters.assigned_to_offline.includes(task?.assigned_to_offline)) {
                newFilters.assigned_to_offline.push(task?.assigned_to_offline);
            }
            if (task?.tag_repr && !newFilters.tag.includes(task?.tag_repr)) {
                newFilters.tag.push(task?.tag_repr);
            }
            if (task?.workstream_repr && !newFilters.workstream.includes(task?.workstream_repr)) {
                newFilters.workstream.push(task?.workstream_repr);
            }
            if (task?.status && !newFilters.status.includes(task?.status)) {
                newFilters.status.push(task?.status);
            }
        });

        return newFilters;
    };
    const tableActiveFilters = filterItemsWithValueFalse(tableFilters);
    return (
        <div>
            {selectedRows?.length > 0 ? (
                <div style={{ zIndex: 2 }} className={styles.dropdownsBody + ' d-flex justify-content-end me-2 flex-wrap align-items-center position-relative '}>
                    <Dropdown>
                        <Dropdown.Toggle as={PriorityToggle} id="dropdown-basic"></Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleChangeBulkPriority('HIGH')}>High</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleChangeBulkPriority('MEDIUM')}>Medium</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleChangeBulkPriority('LOW')}>Low</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                    <Dropdown>
                        <Dropdown.Toggle as={AssinedToToggle} id="dropdown-basic"></Dropdown.Toggle>
                        <Dropdown.Menu>
                            {projectUsersAll?.map(item => (
                                <Dropdown.Item onClick={() => handleChangeBulkAssigned(item?.value?.user_id)} key={item?.value?.user_id} value={item?.value?.user_id}>
                                    {item?.value?.full_name}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    <Dropdown>
                        <Dropdown.Toggle as={TagToggle} id="dropdown-basic"></Dropdown.Toggle>
                        <Dropdown.Menu>
                            {items?.map(item => (
                                <Dropdown.Item onClick={() => handleChangeBulkTag(item?.name)} value={item?.name} key={item?.id}>
                                    {item?.name}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    <DatePicker onChange={e => handleChangeBulkDeadline(e)} customInput={<DeadlineDatePicker />} />

                    <Dropdown>
                        <Dropdown.Toggle as={StatusToggle} id="dropdown-basic"></Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleChangeBulkStatus('COMPLETE')}>Complete</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleChangeBulkStatus('PENDING')}>Pending</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                    <DoubleConfirmationModal
                        message="Deleting a Task will permanently remove it from the Project."
                        handleAction={() => {
                            handleDeleteTasks();
                        }}>
                        <button type="button" className={`btn btn-primary btn-sm ${styles.deleteButton} ${styles.smallButton}`} style={{ height: 26 }}>
                            Delete
                        </button>
                    </DoubleConfirmationModal>
                </div>
            ) : (
                <div className="d-flex align-items-center mb-3 justify-content-between">
                    <div className="d-flex align-items-center  ">
                        <ColumnFilters columnState={columnState} setColumnState={setColumnState} />
                        <p className={'ms-3 ' + styles.questionsLength}>
                            Viewing{' '}
                            <b>
                                {filteredData.length} of {taskData?.length || 0}
                            </b>{' '}
                            Tasks
                        </p>
                    </div>
                    <div className="d-flex flex-wrap">
                        {Object?.keys(tableActiveFilters).map(key => (
                            <div className={styles.filterTag} onClick={() => setAllValuesToTrue(tableFilters, key)}>
                                {key} filter applied <RxCross2 />
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className={`mt-3 ${styles.tableContainer} `} id="style-3-scroll">
                <table className="w-100">
                    <thead className={styles.tableHeader}>
                        <tr>
                            <td className="ps-2 pt-2" style={{ minWidth: 40, width: 40 }}>
                                <input
                                    type="checkbox"
                                    checked={selectedRows?.length === filteredData.length ? true : false}
                                    onChange={e => handleSelectAllFilter(e.target.checked)}
                                />
                            </td>
                            <td style={{ minWidth: 40, width: 40 }}></td>
                            {columnState?.priority.value && (
                                <td style={{ minWidth: 85 }}>
                                    <div className="d-flex align-items-center">
                                        <span> Priority </span>
                                        &nbsp;
                                        <Dropdown>
                                            <Dropdown.Toggle as={FilterIcon} id="dropdown-basic"></Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <div style={{ width: 150 }} className={styles.filterBody}>
                                                    <div className="row">
                                                        {tableFilters?.priority?.map(item => (
                                                            <div key={item?.label} className="col-sm-12 d-flex align-items-center mt-2">
                                                                <input
                                                                    checked={item?.value}
                                                                    onChange={e => handleChangeFilters(item?.label, 'priority', e.target.checked)}
                                                                    type="checkbox"
                                                                    id="flexCheckDefault"
                                                                    className=" me-3"
                                                                />
                                                                <label className={styles.checkLabel + ' ' + styles.smallFont}>{item?.label?.toUpperCase() || 'N/A'}</label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                </td>
                            )}
                            {columnState?.task.value && <td style={{ minWidth: 260 }}>Task</td>}
                            {columnState?.workstream.value && (
                                <td style={{ minWidth: 150 }}>
                                    <div className="d-flex align-items-center">
                                        <span> Workstream </span>
                                        &nbsp;
                                        <Dropdown>
                                            <Dropdown.Toggle as={FilterIcon} id="dropdown-basic"></Dropdown.Toggle>
                                            {tableFilters?.workstream?.length > 0 && (
                                                <Dropdown.Menu>
                                                    <div style={{ width: 250 }} className={styles.filterBody}>
                                                        <div className="row">
                                                            {tableFilters?.workstream?.map(item => (
                                                                <div key={item?.label} className="col-sm-12 d-flex align-items-center mt-2">
                                                                    <input
                                                                        checked={item?.value}
                                                                        onChange={e => handleChangeFilters(item?.label, 'workstream', e.target.checked)}
                                                                        type="checkbox"
                                                                        id="flexCheckDefault"
                                                                        className=" me-3"
                                                                    />
                                                                    <label className={styles.checkLabel + ' ' + styles.smallFont}>{item?.label?.toUpperCase()}</label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </Dropdown.Menu>
                                            )}
                                        </Dropdown>
                                    </div>
                                </td>
                            )}
                            {columnState?.assigned_to.value && (
                                <td style={{ minWidth: 114 }}>
                                    <div className="d-flex align-items-center">
                                        <span> Assigned to </span>
                                        &nbsp;
                                        <Dropdown>
                                            <Dropdown.Toggle as={FilterIcon} id="dropdown-basic"></Dropdown.Toggle>
                                            {(tableFilters?.assigned_to?.length > 0 || tableFilters?.assigned_to_offline?.length > 0) && (
                                                <Dropdown.Menu>
                                                    <div style={{ width: 250 }} className={styles.filterBody}>
                                                        <div className="row">
                                                            {tableFilters?.assigned_to?.map(item => (
                                                                <div key={item?.label} className="col-sm-12 d-flex align-items-center mt-2">
                                                                    <input
                                                                        checked={item?.value}
                                                                        onChange={e => handleChangeFilters(item?.label, 'assigned_to', e.target.checked)}
                                                                        type="checkbox"
                                                                        id="flexCheckDefault"
                                                                        className=" me-3"
                                                                    />
                                                                    <label className={styles.checkLabel + ' ' + styles.smallFont}>{handleGetProjectName(item?.label)}</label>
                                                                </div>
                                                            ))}
                                                            {tableFilters?.assigned_to_offline?.map(item => (
                                                                <div key={item?.label} className="col-sm-12 d-flex align-items-center mt-2">
                                                                    <input
                                                                        checked={item?.value}
                                                                        onChange={e => handleChangeFilters(item?.label, 'assigned_to_offline', e.target.checked)}
                                                                        type="checkbox"
                                                                        id="flexCheckDefault"
                                                                        className=" me-3"
                                                                    />
                                                                    <label className={styles.checkLabel + ' ' + styles.smallFont}>{item?.label}</label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </Dropdown.Menu>
                                            )}
                                        </Dropdown>
                                    </div>
                                </td>
                            )}
                            {columnState?.tag.value && (
                                <td style={{ minWidth: 200 }}>
                                    <div className="d-flex align-items-center">
                                        <span> Tag</span>
                                        &nbsp;
                                        <Dropdown>
                                            <Dropdown.Toggle as={FilterIcon} id="dropdown-basic"></Dropdown.Toggle>
                                            {tableFilters?.tag?.length > 0 && (
                                                <Dropdown.Menu>
                                                    <div style={{ width: 250 }} className={styles.filterBody}>
                                                        <div className="row">
                                                            {tableFilters?.tag?.map(item => (
                                                                <div key={item?.label} className="col-sm-12 d-flex align-items-center mt-2">
                                                                    <input
                                                                        checked={item?.value}
                                                                        onChange={e => handleChangeFilters(item?.label, 'tag', e.target.checked)}
                                                                        type="checkbox"
                                                                        id="flexCheckDefault"
                                                                        className=" me-3"
                                                                    />
                                                                    <label className={styles.checkLabel + ' ' + styles.smallFont}>{item?.label?.toUpperCase()}</label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </Dropdown.Menu>
                                            )}
                                        </Dropdown>
                                    </div>
                                </td>
                            )}
                            {columnState?.added_on.value && (
                                <td style={{ minWidth: 100 }}>
                                    <div className="d-flex align-items-center">
                                        <span> Added on </span>
                                        &nbsp;
                                        <TiArrowUnsorted onClick={handleSortAdded} className="me-2" color="#829ab1" size={14} role="button" />
                                    </div>
                                </td>
                            )}
                            {columnState?.deadline.value && (
                                <td style={{ minWidth: 150 }}>
                                    <div className="d-flex align-items-center">
                                        <span> Deadline </span>
                                        &nbsp;
                                        <TiArrowUnsorted onClick={handleSortDeadline} className="me-2" color="#829ab1" size={14} role="button" />
                                    </div>
                                </td>
                            )}
                            {columnState?.status.value && (
                                <td style={{ minWidth: 102 }}>
                                    <div className="d-flex align-items-center">
                                        <span> Status</span>
                                        &nbsp;
                                        <div className={styles.kebabBody}>
                                            <Dropdown>
                                                <Dropdown.Toggle as={FilterIcon} id="dropdown-basic"></Dropdown.Toggle>
                                                {tableFilters?.status?.length > 0 && (
                                                    <Dropdown.Menu>
                                                        <div style={{ width: 120 }} className={styles.filterBody}>
                                                            <div className="row">
                                                                {tableFilters?.status?.map(
                                                                    item =>
                                                                        item?.label && (
                                                                            <div key={item?.label} className="col-sm-12 d-flex align-items-center mt-2">
                                                                                <input
                                                                                    checked={item?.value}
                                                                                    onChange={e => handleChangeFilters(item?.label, 'status', e.target.checked)}
                                                                                    type="checkbox"
                                                                                    id="flexCheckDefault"
                                                                                    className=" me-3"
                                                                                />
                                                                                <label className={styles.checkLabel + ' ' + styles.smallFont}>{item?.label?.toUpperCase()}</label>
                                                                            </div>
                                                                        ),
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Dropdown.Menu>
                                                )}
                                            </Dropdown>
                                        </div>
                                    </div>
                                </td>
                            )}
                            <th style={{ minWidth: 40 }}> &nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData?.map(item => (
                            <TaskRow
                                key={item?.id}
                                item={item}
                                setViewModalVisible={setViewModalVisible}
                                setSelectedRow={setSelectedRow}
                                setModalVisible={setModalVisible}
                                columnState={columnState}
                                selectedRows={selectedRows}
                                setSelectedRows={setSelectedRows}
                                filteredData={filteredData}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const TaskRow = ({ item, setViewModalVisible, setSelectedRow, setModalVisible, columnState, selectedRows, setSelectedRows, filteredData }) => {
    const dispatch = useDispatch();
    const { projectUsersAll } = useSelector(state => state.team);
    const { taskData } = useSelector(state => state.task);
    const [isHovered, setIsHovered] = React.useState(false);
    const [starLoading, setStarLoading] = React.useState(false);

    const CustomToggle = React.forwardRef(({ onClick }, ref) => (
        <BsThreeDotsVertical
            ref={ref}
            color="rgba(130, 154, 177, 0.5)"
            onClick={e => {
                e.preventDefault();
                onClick(e);
            }}
        />
    ));
    const handleSelectRows = id => {
        if (selectedRows?.find(x => x === id)) {
            setSelectedRows(selectedRows.filter(x => x !== id));
        } else {
            setSelectedRows([...selectedRows, id]);
        }
    };

    const handleGetProjectName = id => {
        let user = projectUsersAll.find(item => item.value.user_id === id);
        return (
            <div>
                {user?.value?.full_name}
                {/* <p className="m-0" style={{ color: '#829ab1' }}>
                    {user?.value?.organisation}
                </p> */}
            </div>
        );
    };

    const handleShowDeadline = item => {
        const check = isAfter(new Date(), new Date(item?.deadline));
        const days = Math.abs(differenceInDays(new Date(), new Date(item?.deadline)));
        if (days === 0 || days === 1) {
            return (
                <p
                    style={{
                        color: check ? '#da2a2a' : '#33b5e5',
                        fontWeight: check && 'bold',
                    }}
                    className={styles.smallFont + ' m-0'}>
                    {check ? '1 day overdue' : '1 day remaining'}
                </p>
            );
        } else {
            return (
                <p
                    style={{
                        color: check ? '#da2a2a' : '#33b5e5',
                        fontWeight: check && 'bold',
                    }}
                    className={styles.smallFont + ' m-0'}>
                    {check ? `${days} days overdue` : days + ' days remaining'}
                </p>
            );
        }
    };
    const handleShowCompleted = item => {
        const days = differenceInDays(new Date(item?.completed), new Date(item?.created));

        if (days === 0 || days === 1) {
            return `Completed in 1 day`;
        } else if (days > 1) {
            return `Completed in ${days} days`;
        }
    };
    const handleSelectRow = () => {
        setSelectedRow(item);
        setViewModalVisible(true);
    };
    const handleStarClick = async e => {
        e.stopPropagation();
        if (starLoading) return;
        setStarLoading(true);
        if (!item.starred) {
            // Unstar any other starred task
            const otherStarred = taskData?.find(t => t.id !== item.id && t.starred);
            if (otherStarred) {
                await dispatch(PatchTask({ body: { starred: false }, id: otherStarred.id, suppressToast: true }));
            }
            await dispatch(PatchTask({ body: { starred: true }, id: item.id }));
        } else {
            // Unstar this task
            await dispatch(PatchTask({ body: { starred: false }, id: item.id }));
        }
        setStarLoading(false);
    };
    return (
        <tr key={item?.id} className={styles.tableRow} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <td className={styles.rowItem + ' ps-2'} style={{ minWidth: 40, width: 40 }}>
                <input
                    className={styles.rowCheck}
                    type="checkbox"
                    style={{ display: selectedRows?.find(x => x === item?.id) && 'block' }}
                    checked={selectedRows?.find(x => x === item?.id)}
                    onChange={e => handleSelectRows(item?.id, e.target.checked)}
                />
            </td>
            <td className={styles.rowItem} style={{ minWidth: 40, width: 40, verticalAlign: 'middle' }}>
                <div className="d-flex align-items-center" style={{ height: 32 }}>
                    {(isHovered || item.starred) &&
                        (item.starred ? (
                            <AiFillStar
                                size={18}
                                color="#FFD600"
                                role="button"
                                style={{ verticalAlign: 'middle', cursor: starLoading ? 'wait' : 'pointer', marginTop: -11 }}
                                onClick={handleStarClick}
                            />
                        ) : (
                            <AiOutlineStar
                                size={18}
                                color="var(--Brand-Secondary)"
                                role="button"
                                style={{ verticalAlign: 'middle', cursor: starLoading ? 'wait' : 'pointer', marginTop: -11 }}
                                onClick={handleStarClick}
                            />
                        ))}
                </div>
            </td>
            {columnState?.priority.value && (
                <td onClick={handleSelectRow} className={styles.rowItem}>
                    <span
                        style={{
                            color: (item?.priority === 'HIGH' && '#da2a2a') || (item?.priority === 'MEDIUM' && '#ffbb33') || (item?.priority === 'LOW' && '#33b5e5'),
                        }}>
                        {item?.priority}
                    </span>
                </td>
            )}
            {columnState?.task.value && (
                <td onClick={handleSelectRow} className={styles.rowItem}>
                    <div className={styles?.dropdown}>
                        <div style={{ minWidth: '400px', maxWidth: '400px' }}>
                            <p className="text-truncate m-0">{htmlToPlainText(item?.task || '')}</p>
                        </div>
                        <div className={styles.hoverMenuContent}>
                            <div
                                style={{
                                    width: '400px',
                                }}
                                className={styles.hoverMenuCont}>
                                {htmlToPlainText(item?.task || '')}
                            </div>
                        </div>
                    </div>
                </td>
            )}
            {columnState?.workstream.value && (
                <td onClick={handleSelectRow} className={styles.rowItem}>
                    <span>{item?.workstream_repr || '-'}</span>
                </td>
            )}
            {columnState?.assigned_to.value && (
                <td onClick={handleSelectRow} className={styles.rowItem}>
                    {(item?.assigned_to && <div>{handleGetProjectName(item?.assigned_to)}</div>) || (item?.assigned_to_offline && <p>{item?.assigned_to_offline}</p>)}
                </td>
            )}
            {columnState?.tag.value && (
                <td onClick={handleSelectRow} className={styles.rowItem}>
                    <span>{item?.tag_repr}</span>
                </td>
            )}
            {columnState?.added_on.value && (
                <td onClick={handleSelectRow} className={styles.rowItem}>
                    <p className={styles.smallFont + ' m-0'}>{item?.created && format(new Date(item?.created), 'dd/MM/yyyy')}</p>
                </td>
            )}
            {columnState?.deadline.value && (
                <td onClick={handleSelectRow} className={styles.rowItem}>
                    <div style={{ opacity: item?.assigned_to || item?.assigned_to_offline ? 1 : 0.6, width: 150 }}>
                        {item?.status === 'COMPLETE' ? (
                            <>
                                <p className={styles.smallFont + ' m-0'}>{item?.deadline && format(new Date(item?.deadline), 'dd/MM/yyyy')}</p>
                                <p style={{ color: '#33b5e5' }} className={styles.smallFont + ' m-0'}>
                                    {item?.deadline && handleShowCompleted(item)}
                                </p>
                            </>
                        ) : (
                            <>
                                {' '}
                                <p className={styles.smallFont + ' m-0'}>{item?.deadline && format(new Date(item?.deadline), 'dd/MM/yyyy')}</p>
                                {item?.deadline && handleShowDeadline(item)}
                            </>
                        )}
                    </div>
                </td>
            )}
            {columnState?.status.value && (
                <td onClick={handleSelectRow} className={styles.rowItem}>
                    {item?.status === 'COMPLETE' ? (
                        <span style={{ color: '#00c851' }}>Completed</span>
                    ) : item?.status === 'PENDING' ? (
                        <span style={{ color: '#ff0000' }}>Pending</span>
                    ) : null}
                </td>
            )}
            <td className={styles.rowItem}>
                <div className={styles.kebabBody}>
                    <Dropdown>
                        <Dropdown.Toggle as={CustomToggle} id="dropdown-basic"></Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item
                                onClick={() => {
                                    setSelectedRow(item);
                                    setModalVisible(true);
                                }}>
                                Edit
                            </Dropdown.Item>
                            <DoubleConfirmationModal
                                message="Deleting an Task will permanently remove it and all associated comments from the DD Tool."
                                handleAction={() => dispatch(DeleteTask({ id: item?.id }))}>
                                <Dropdown.Item>Delete</Dropdown.Item>
                            </DoubleConfirmationModal>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </td>
        </tr>
    );
};

export const TaskFormSidebar = ({ show, setShow, selectedRow, setSelectedRow, defaultTask, setDefaultData }) => {
    const dispatch = useDispatch();
    const { projectUsersAll } = useSelector(state => state.team);
    const { user } = useSelector(state => state.user);
    const { taskButtonLoading, projectWorkstreams, projectTags, taskData } = useSelector(state => state.task);
    const { workstreams, default_folders, items } = useSelector(state => state.dataroom);
    const [selectedAssignee, setSelectedAssignee] = React.useState();
    const [task, setTask] = React.useState('');
    const [showMoreDetails, setShowMoreDetails] = React.useState(false);
    const [dataroomTags, setDataroomTags] = React.useState([]);
    const [formData, setFormData] = React.useState({
        deadline: '',
        project: localStorage['project_id'],
        status: 'NOT_STARTED',
        priority: '',
        workstream_repr: '',
        tag_repr: '',
    });
    // Get all workstreams from different sources (same as WorkstreamManagementMenu)
    const getAllWorkstreams = () => {
        const workstreamMap = new Map();

        // 1. Project workstreams
        if (projectWorkstreams && projectWorkstreams.length > 0) {
            projectWorkstreams.forEach(ws => {
                workstreamMap.set(ws.name, { ...ws, source: 'project' });
            });
        }

        // 2. Dataroom workstreams
        if (workstreams && workstreams.length > 0) {
            workstreams.forEach(ws => {
                workstreamMap.set(ws.name, { ...ws, source: 'dataroom' });
            });
        }

        // 3. All workstreams from task data
        const getAllWorkstreamsFromTaskData = () => {
            // Handle both array and object structures
            let tasks = [];
            if (Array.isArray(taskData)) {
                tasks = taskData;
            } else if (taskData && taskData.data && Array.isArray(taskData.data)) {
                tasks = taskData.data;
            } else if (taskData && taskData.groups && Array.isArray(taskData.groups)) {
                // Handle grouped data - flatten all groups
                tasks = taskData.groups.flatMap(group => group.tasks || []);
            } else {
                return [];
            }

            return tasks
                .map(task => task.workstream_repr)
                .filter(workstream => workstream && workstream.trim() !== '')
                .filter((workstream, index, arr) => arr.indexOf(workstream) === index); // Remove duplicates
        };

        const taskWorkstreamNames = getAllWorkstreamsFromTaskData();
        taskWorkstreamNames.forEach(workstreamName => {
            if (!workstreamMap.has(workstreamName)) {
                workstreamMap.set(workstreamName, {
                    id: `task_${workstreamName}`,
                    name: workstreamName,
                    source: 'task',
                });
            }
        });

        return Array.from(workstreamMap.values());
    };

    // Check if selected workstream is from dataroom
    const isDataroomWorkstream = formData.workstream_repr && workstreams?.some(ws => ws.name === formData.workstream_repr);

    // Fetch dataroom data when workstream changes
    const fetchDataroomData = React.useCallback(() => {
        if (isDataroomWorkstream && formData.workstream_repr) {
            // Get the workstream object
            const selectedWorkstream = workstreams?.find(ws => ws.name === formData.workstream_repr) ||
                projectWorkstreams?.find(ws => ws.name === formData.workstream_repr) || {
                    id: `custom_${formData.workstream_repr}`,
                    name: formData.workstream_repr,
                    source: 'custom',
                };

            if (selectedWorkstream?.id) {
                dispatch(GetDataroomItemInfo(selectedWorkstream.id));
                dispatch(GetFolderDefaults(selectedWorkstream.name));
            }
        }
    }, [isDataroomWorkstream, formData.workstream_repr, workstreams, projectWorkstreams, dispatch]);

    // Update dataroom tags when default_folders or items change
    React.useEffect(() => {
        if (isDataroomWorkstream) {
            const combinedOptions = [...new Set([...default_folders.map(folder => folder?.ws_folder), ...items.map(item => item?.name)])].map(label => ({
                id: `dataroom_${label}`,
                name: label,
                source: 'dataroom',
            }));
            setDataroomTags(combinedOptions);
        } else {
            setDataroomTags([]);
        }
    }, [default_folders, items, isDataroomWorkstream]);

    // Fetch dataroom data when workstream changes
    React.useEffect(() => {
        if (formData.workstream_repr) {
            fetchDataroomData();
        }
    }, [formData.workstream_repr, fetchDataroomData]);

    // Get all tags (same as TagManagementMenu)
    const getAllTags = () => {
        const tagMap = new Map();

        // 1. Dataroom tags (when workstream is from dataroom)
        if (isDataroomWorkstream && dataroomTags.length > 0) {
            dataroomTags.forEach(tag => {
                tagMap.set(tag.name, { ...tag, source: 'dataroom' });
            });
        }

        // 2. Project tags
        if (projectTags && projectTags.length > 0) {
            projectTags.forEach(tag => {
                tagMap.set(tag.name, { ...tag, source: 'project' });
            });
        }

        // 3. All tags from task data
        const getAllTagsFromTaskData = () => {
            // Handle both array and object structures
            let tasks = [];
            if (Array.isArray(taskData)) {
                tasks = taskData;
            } else if (taskData && taskData.data && Array.isArray(taskData.data)) {
                tasks = taskData.data;
            } else if (taskData && taskData.groups && Array.isArray(taskData.groups)) {
                // Handle grouped data - flatten all groups
                tasks = taskData.groups.flatMap(group => group.tasks || []);
            } else {
                return [];
            }

            return tasks
                .map(task => task.tag_repr)
                .filter(tag => tag && tag.trim() !== '')
                .filter((tag, index, arr) => arr.indexOf(tag) === index); // Remove duplicates
        };

        const taskTagNames = getAllTagsFromTaskData();
        taskTagNames.forEach(tagName => {
            if (!tagMap.has(tagName)) {
                tagMap.set(tagName, {
                    id: `task_${tagName}`,
                    name: tagName,
                    source: 'task',
                });
            }
        });

        return Array.from(tagMap.values());
    };

    const handleSubmit = async () => {
        if (showMoreDetails) {
            // Expanded view - include all fields
            let body = {
                ...formData,
                task,
                created_by: user?.id,
                workstream_repr: formData?.workstream_repr,
                tag_repr: formData?.tag_repr,
            };
            if (selectedAssignee) {
                if (selectedAssignee?.__isNew__) body.assigned_to_offline = selectedAssignee?.value;
                else body.assigned_to = selectedAssignee?.value?.user_id;
            }
            if (body.deadline === '') delete body.deadline;
            await dispatch(PostTask(body));
        } else {
            // Simplified view - only essential fields like task management table
            let body = {
                task,
                project: localStorage['project_id'],
            };
            await dispatch(PostTask(body));
        }
        handleClose();
    };

    const handleClose = () => {
        setShow(false);
        setSelectedRow && setSelectedRow(null);
        setFormData({
            deadline: '',
            assigned_to: '',
            project: localStorage['project_id'],
            status: 'NOT_STARTED',
            priority: '',
            workstream_repr: '',
            tag_repr: '',
        });
        setSelectedAssignee();
        setTask('');
        setShowMoreDetails(false);
        setDefaultData && setDefaultData();
    };

    React.useEffect(() => {
        if (defaultTask) setTask(defaultTask);
    }, [defaultTask]);

    const allWorkstreams = getAllWorkstreams();
    const allTags = getAllTags();

    return (
        <>
            <OverlayDrawer style={{ minWidth: 400 }} type="overlay" position="end" open={show} onOpenChange={(_, { open }) => handleClose(open)}>
                <DrawerBody style={{ padding: 16 }}>
                    <div className="">
                        <div className="d-flex justify-content-between">
                            <p className={styles.sidebarTitle}>Add New Task</p>
                            <Dismiss20Regular role="button" onClick={handleClose} color="var(--Neutral-Gray-6)" />
                        </div>

                        <div className="mt-4">
                            {/* Task Description - Always visible */}
                            <div className="mb-4">
                                <textarea
                                    className="form-control"
                                    placeholder="Type your task here..."
                                    value={task}
                                    onChange={e => setTask(e.target.value)}
                                    rows={4}
                                    style={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        fontSize: '14px',
                                        resize: 'vertical',
                                        minHeight: '100px',
                                    }}
                                />
                            </div>

                            {/* Action Buttons - Only show when not in expanded view */}
                            {!showMoreDetails && (
                                <div className="d-flex justify-content-between align-items-center">
                                    <button
                                        disabled={!task.trim() || taskButtonLoading}
                                        onClick={handleSubmit}
                                        className="btn btn-primary"
                                        style={{
                                            backgroundColor: 'var(--Brand-Secondary)',
                                            borderRadius: '6px',
                                            padding: '8px 16px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            border: 'none',
                                        }}>
                                        Add Task {taskButtonLoading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setShowMoreDetails(true)}
                                        className="btn btn-link"
                                        style={{
                                            color: 'var(--Brand-Secondary)',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                        }}>
                                        Add more details
                                    </button>
                                </div>
                            )}

                            {/* Expanded Form Fields */}
                            {showMoreDetails && (
                                <div className="mt-4">
                                    {/* Owner Field */}
                                    <div className="mb-3">
                                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                            Owner
                                        </label>
                                        <Select
                                            name="selectedAssignee"
                                            options={projectUsersAll?.map(item => {
                                                return { label: item?.value?.full_name, value: item?.value };
                                            })}
                                            className="basic-multi-select"
                                            classNamePrefix="select"
                                            value={selectedAssignee}
                                            onChange={e => setSelectedAssignee(e)}
                                            placeholder="Select team member"
                                            maxMenuHeight={200}
                                            isClearable
                                            styles={{
                                                control: (provided, state) => ({
                                                    ...provided,
                                                    borderColor: '#e0e0e0',
                                                    borderRadius: '6px',
                                                    minHeight: '36px',
                                                    fontSize: 14,
                                                }),
                                                valueContainer: (provided, state) => ({
                                                    ...provided,
                                                    height: '36px',
                                                    padding: '0 8px',
                                                }),
                                                input: (provided, state) => ({
                                                    ...provided,
                                                    margin: '0px',
                                                }),
                                                indicatorSeparator: state => ({
                                                    display: 'none',
                                                }),
                                                indicatorsContainer: (provided, state) => ({
                                                    ...provided,
                                                    height: '34px',
                                                }),
                                            }}
                                        />
                                    </div>

                                    {/* Priority and Status Row */}
                                    <div className="row mb-3">
                                        <div className="col-6">
                                            <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                                Priority
                                            </label>
                                            <select
                                                name="priority"
                                                value={formData.priority}
                                                onChange={e => setFormData({ ...formData, priority: e.target.value })}
                                                className="form-select"
                                                style={{
                                                    borderColor: '#e0e0e0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px',
                                                    height: '36px',
                                                }}>
                                                <option value="">Select priority</option>
                                                <option value="HIGH">High</option>
                                                <option value="MEDIUM">Medium</option>
                                                <option value="LOW">Low</option>
                                            </select>
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                                Status
                                            </label>
                                            <select
                                                name="status"
                                                value={formData.status}
                                                onChange={e => setFormData({ ...formData, status: e.target.value })}
                                                className="form-select"
                                                style={{
                                                    borderColor: '#e0e0e0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px',
                                                    height: '36px',
                                                }}>
                                                <option value="NOT_STARTED">Not Started</option>
                                                <option value="WORKING_ON_IT">Working on it</option>
                                                <option value="STUCK">Stuck</option>
                                                <option value="COMPLETED">Completed</option>
                                                <option value="CANCELLED">Cancelled</option>
                                                <option value="UNDEFINED">Undefined</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Workstream Field */}
                                    <div className="mb-3">
                                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                            Workstream
                                        </label>
                                        <select
                                            name="workstream_repr"
                                            value={formData.workstream_repr}
                                            onChange={e => setFormData({ ...formData, workstream_repr: e.target.value })}
                                            className="form-select"
                                            style={{
                                                borderColor: '#e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                height: '36px',
                                            }}>
                                            <option value="">Select Workstream</option>
                                            {allWorkstreams?.map(item => (
                                                <option key={item?.id} value={item?.name}>
                                                    {item?.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Tag Field */}
                                    <div className="mb-3">
                                        <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                            Tag
                                        </label>
                                        <select
                                            name="tag_repr"
                                            value={formData.tag_repr}
                                            onChange={e => setFormData({ ...formData, tag_repr: e.target.value })}
                                            className="form-select"
                                            style={{
                                                borderColor: '#e0e0e0',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                height: '36px',
                                            }}>
                                            <option value="">Select Tag</option>
                                            {allTags?.map(item => (
                                                <option key={item?.id} value={item?.name}>
                                                    {item?.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Deadline Row */}
                                    <div className="row mb-4">
                                        <div className="col-6">
                                            <label className="form-label" style={{ fontSize: '12px', fontWeight: '500', marginBottom: '3px' }}>
                                                Deadline
                                            </label>
                                            <input
                                                type="date"
                                                name="deadline"
                                                value={formData.deadline}
                                                onChange={e => setFormData({ ...formData, deadline: e.target.value })}
                                                className="form-control"
                                                style={{
                                                    borderColor: '#e0e0e0',
                                                    borderRadius: '6px',
                                                    fontSize: '14px',
                                                    height: '36px',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Action Buttons for Expanded View */}
                                    <div className="d-flex justify-content-between align-items-center">
                                        <button
                                            disabled={!task.trim() || taskButtonLoading}
                                            onClick={handleSubmit}
                                            className="btn btn-primary"
                                            style={{
                                                backgroundColor: 'var(--Brand-Secondary)',
                                                borderRadius: '6px',
                                                padding: '8px 16px',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                                border: 'none',
                                            }}>
                                            Add Task {taskButtonLoading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => setShowMoreDetails(false)}
                                            className="btn btn-link"
                                            style={{
                                                color: 'var(--Brand-Secondary)',
                                                textDecoration: 'none',
                                                fontSize: '14px',
                                                fontWeight: '500',
                                            }}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </DrawerBody>
            </OverlayDrawer>
        </>
    );
};

const TaskViewSidebar = ({ show, setShow, selectedRow, setSelectedRow, setEditModalVisible }) => {
    const dispatch = useDispatch();
    const { taskComments, taskCommentBtnLoading } = useSelector(state => state.task);
    const { user } = useSelector(state => state.user);
    const { projectUsersAll } = useSelector(state => state.team);
    const [comment, setComment] = React.useState('');
    const [showComment, setShowComment] = React.useState(false);

    React.useEffect(() => {
        setShowComment(false);
        if (selectedRow) dispatch(GetTaskComment({ taskId: selectedRow?.id }));
    }, [selectedRow, dispatch]);
    const handleSubmit = async () => {
        let body = {
            task: selectedRow?.id,
            comment,
            created_by: user?.id,
        };
        dispatch(PostTaskComment({ body }));
        setComment('');
        setShowComment(false);
    };

    const handleClose = () => {
        setShow(false);
        setComment('');
        setSelectedRow(null);
    };

    const handleMarkComplete = () => {
        let body = {
            project: localStorage['project_id'],
            status: 'COMPLETE',
            completed: format(new Date(), 'yyyy-MM-dd'),
        };
        if (selectedRow?.starred) {
            body.starred = false;
        }
        dispatch(PatchTask({ body, id: selectedRow?.id }));
        handleClose();
    };

    const handleGetProjectName = (id, onlyName) => {
        let userData = projectUsersAll.find(item => item.value.user_id === id);
        if (!userData && user?.id === id) {
            userData = {
                value: { ...user },
            };
        }

        return onlyName ? (
            <span> {userData?.value?.full_name}</span>
        ) : (
            <div className="d-flex align-items-center">
                <div className={styles.smallAvatar} style={{ background: userData?.color }}>
                    {getProfileNameComponent(userData?.value?.full_name)}
                </div>
                <span style={{ maxWidth: 120 }} className={styles.smallFont + ' m-0 ms-2 text-truncate'}>
                    {userData?.value?.full_name}
                </span>
            </div>
        );
    };
    const handleGetProjectDetail = id => {
        let user = projectUsersAll.find(item => item.value.user_id === id);
        return (
            <>
                <b className={styles.regularText}>{user?.value?.full_name || selectedRow?.assigned_to_offline}</b>
                <p className={styles.smallFont + ' m-0 ' + styles.lightGray}>{user?.value?.organisation}</p>
            </>
        );
    };
    const CustomToggle = React.forwardRef(({ onClick }, ref) => (
        <BsThreeDotsVertical
            ref={ref}
            color="rgba(130, 154, 177, 0.5)"
            onClick={e => {
                e.preventDefault();
                onClick(e);
            }}
        />
    ));

    return (
        <>
            <Offcanvas placement="end" show={show} onHide={handleClose}>
                <Offcanvas.Body>
                    <div className="p-3">
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                {selectedRow?.status === 'COMPLETE' ? (
                                    <div className={styles.completeDiv}>Completed</div>
                                ) : (
                                    <button onClick={handleMarkComplete} className={'btn btn-primary smallButton'}>
                                        Mark as Complete
                                    </button>
                                )}

                                <div className={styles.kebabBody}>
                                    <Dropdown>
                                        <Dropdown.Toggle as={CustomToggle} id="dropdown-basic"></Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item
                                                onClick={() => {
                                                    // setSelectedRow(item);
                                                    // setModalVisible(true);
                                                    setShow(false);
                                                    setEditModalVisible(true);
                                                }}>
                                                Edit
                                            </Dropdown.Item>
                                            {/* <DeleteMenuModal
                                                handleAction={() => {
                                                    dispatch(DeleteTask({ id: selectedRow?.id }));
                                                    handleClose();
                                                }}
                                            /> */}
                                            <DoubleConfirmationModal
                                                message="Deleting an Task will permanently remove it and all associated comments from the DD Tool."
                                                handleAction={() => {
                                                    dispatch(DeleteTask({ id: selectedRow?.id }));
                                                    handleClose();
                                                }}>
                                                <Dropdown.Item>Delete</Dropdown.Item>
                                            </DoubleConfirmationModal>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </div>
                            <div className="mb-5">
                                <p
                                    className={styles.blueLabel}
                                    style={{
                                        color:
                                            (selectedRow?.priority === 'HIGH' && '#da2a2a') ||
                                            (selectedRow?.priority === 'MEDIUM' && '#fc9c24') ||
                                            (selectedRow?.priority === 'LOW' && '#33b5e5'),
                                    }}>
                                    {selectedRow?.priority?.toLowerCase()} priority
                                </p>
                                <p
                                    className={styles.regularText}
                                    dangerouslySetInnerHTML={{
                                        __html: selectedRow?.task,
                                    }}></p>
                            </div>
                            <div className="row">
                                <div className="col-sm-12 col-md-6">
                                    <p className={styles.blueLabel + ' mb-1'}>Deadline</p>
                                    <div style={{ lineHeight: 1.2 }}>
                                        <b className={styles.medFont}>{selectedRow?.deadline && format(new Date(selectedRow?.deadline), 'dd/MM/yyyy')}</b>
                                        <p className={styles.smallFont + ' m-0 ' + styles.lightGray}>
                                            {selectedRow?.deadline && differenceInDays(new Date(selectedRow?.deadline), new Date())} days remaining
                                        </p>
                                    </div>
                                </div>
                                <div className="col-sm-12 col-md-6">
                                    <p className={styles.blueLabel + ' mb-1'}>Assigned to</p>
                                    <div style={{ lineHeight: 1.2 }}>{handleGetProjectDetail(selectedRow?.assigned_to)}</div>
                                </div>
                            </div>
                            <div className={styles.dashLine}></div>
                            <div className={styles.dashLine}></div>
                            {selectedRow?.created_by && (
                                <div className={styles.smallFont + ' ' + styles.lightGray}>
                                    Added on {selectedRow?.created && format(new Date(selectedRow?.created), 'dd/MM/yyyy')} by {handleGetProjectName(selectedRow?.created_by, true)}{' '}
                                </div>
                            )}
                            <div className={styles.dashLine}></div>
                            {taskComments?.length > 0 && (
                                <div className="mt-4">
                                    <p className={styles.blueLabel}>Comments</p>
                                    {taskComments?.map(item => (
                                        <div key={item?.id} className="mb-4">
                                            <SafeHTML 
                                                html={item?.comment} 
                                                className={styles.observationText + ' mb-2 ' + styles.regularText}
                                                tag="p"
                                            />
                                            <div className={styles.smallFont + ' ' + styles.lightGray}>
                                                Added on {item?.created && format(new Date(item?.created), 'dd/MM/yyyy')} by {handleGetProjectName(item?.created_by, true)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {showComment ? (
                                <>
                                    <div className="mt-5">
                                        <p htmlFor style={{ marginBottom: -36 }} className={'quilLabel ' + styles.smallFont}>
                                            Add comment
                                        </p>
                                        <ReactQuill
                                            placeholder={'Type here'}
                                            modules={{
                                                toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                                            }}
                                            theme="snow"
                                            className="mt-1"
                                            defaultValue={comment}
                                            onChange={t => {
                                                if (t === '<p><br></p>') setComment('');
                                                else setComment(t);
                                            }}
                                        />
                                    </div>
                                    <div className="mt-4 d-flex justify-content-between align-items-center">
                                        <button
                                            onClick={handleSubmit}
                                            disabled={!comment}
                                            type="button"
                                            className={`btn btn-primary btn-sm  ${styles.responseButton} ${styles.smallButton} ${!comment && styles.disabledSaveBtn}`}>
                                            Save &nbsp; {taskCommentBtnLoading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <button onClick={() => setShowComment(true)} type="button" className={`btn btn-primary btn-sm  ${styles.responseButton} ${styles.smallButton}`}>
                                    Add Comment
                                </button>
                            )}
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default TaskInterface;
